import { useCallback, useEffect, useMemo, useState } from "react";
import {
  COEXISTENCE_PROVIDER_SLUGS,
  type CoexistenceInstructionPlan,
  type CoexistenceInstructionStepView,
  type CoexistenceProviderSlug,
  getCoexistenceInstructions,
  usePageDomains,
} from "@/hooks/use-page-domains";

/**
 * DOM-004 Phase 3 — Coexistence-mode DNS instruction surface.
 *
 * Renders the deterministic 4-step plan (ownership TXT → web CNAME →
 * email MX warning → SPF warning) emitted by the Phase 1 generator and
 * hydrated by the Phase 2 route `GET /domains/:id/coexistence-instructions`.
 * The operator picks a domain + registrar; the server collapses unknown
 * registrars to `generic` and computes a deterministic
 * `stero-verify=<32-hex>` ownership token from `(tenantId, domainId)` so
 * the future verification poller can recompute the same TXT value
 * without a schema column.
 *
 * No DNS is mutated from this surface — coexistence mode is, by
 * definition, non-breaking. The customer copies the steps into their
 * registrar's DNS UI by hand.
 */

const PROVIDER_LABELS: Record<CoexistenceProviderSlug, string> = {
  godaddy: "GoDaddy",
  namecheap: "Namecheap",
  cloudflare: "Cloudflare",
  google_domains: "Google Domains",
  squarespace_domains: "Squarespace Domains",
  generic: "Other / Generic",
};

const RECORD_TYPE_BADGE: Record<
  CoexistenceInstructionStepView["recordType"],
  string
> = {
  TXT: "bg-blue-100 text-blue-900",
  CNAME: "bg-emerald-100 text-emerald-900",
  MX: "bg-amber-100 text-amber-900",
  NOTE: "bg-muted text-muted-foreground",
};

export default function DomainDnsPage() {
  const { domains, isLoading: domainsLoading, error: domainsError } =
    usePageDomains();
  const [selectedDomainId, setSelectedDomainId] = useState<string>("");
  const [provider, setProvider] = useState<CoexistenceProviderSlug>("generic");
  const [plan, setPlan] = useState<CoexistenceInstructionPlan | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);

  const orderedDomains = useMemo(
    () => [...domains].sort((a, b) => a.domain.localeCompare(b.domain)),
    [domains],
  );

  useEffect(() => {
    if (selectedDomainId !== "") return;
    const first = orderedDomains[0];
    if (first) {
      setSelectedDomainId(first.id);
    }
  }, [orderedDomains, selectedDomainId]);

  const loadPlan = useCallback(async () => {
    if (selectedDomainId === "") return;
    setPlanLoading(true);
    setPlanError(null);
    try {
      const result = await getCoexistenceInstructions(
        selectedDomainId,
        provider,
      );
      setPlan(result);
    } catch {
      setPlan(null);
      setPlanError(
        "Failed to load coexistence DNS instructions for this domain.",
      );
    } finally {
      setPlanLoading(false);
    }
  }, [selectedDomainId, provider]);

  return (
    <div className="max-w-3xl space-y-6">
      <header className="space-y-1">
        <h2 className="text-lg font-semibold text-foreground">
          Coexistence DNS instructions
        </h2>
        <p className="text-sm text-muted-foreground">
          Keep DNS at your existing registrar and point only the platform
          subdomain at Stero. Copy the 4-step plan below into your registrar
          dashboard. No existing email or website records are modified.
        </p>
      </header>

      <section className="grid gap-3 sm:grid-cols-3">
        <label className="space-y-1 sm:col-span-2">
          <span className="text-xs font-medium uppercase text-muted-foreground">
            Domain
          </span>
          <select
            data-testid="coexistence-domain-select"
            disabled={domainsLoading || orderedDomains.length === 0}
            value={selectedDomainId}
            onChange={(e) => {
              setSelectedDomainId(e.target.value);
              setPlan(null);
            }}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            {orderedDomains.length === 0 ? (
              <option value="">No mounted custom domains</option>
            ) : (
              orderedDomains.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.domain}
                </option>
              ))
            )}
          </select>
        </label>

        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">
            Registrar
          </span>
          <select
            data-testid="coexistence-provider-select"
            value={provider}
            onChange={(e) => {
              setProvider(e.target.value as CoexistenceProviderSlug);
              setPlan(null);
            }}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            {COEXISTENCE_PROVIDER_SLUGS.map((slug) => (
              <option key={slug} value={slug}>
                {PROVIDER_LABELS[slug]}
              </option>
            ))}
          </select>
        </label>
      </section>

      <div>
        <button
          type="button"
          onClick={() => void loadPlan()}
          disabled={selectedDomainId === "" || planLoading}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {planLoading ? "Loading instructions..." : "Show coexistence plan"}
        </button>
      </div>

      {domainsError && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {domainsError}
        </p>
      )}
      {planError && (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {planError}
        </p>
      )}

      {plan && <CoexistencePlanView plan={plan} />}
    </div>
  );
}

function CoexistencePlanView({ plan }: { plan: CoexistenceInstructionPlan }) {
  return (
    <section
      data-testid="coexistence-plan"
      className="space-y-4 rounded-md border border-border p-4"
    >
      <header className="space-y-1">
        <p className="text-sm font-medium text-foreground">
          {plan.domain}{" "}
          <span className="text-xs text-muted-foreground">
            ({PROVIDER_LABELS[plan.provider]} · apex {plan.apexDomain})
          </span>
        </p>
        <p className="text-xs text-muted-foreground">
          Platform CNAME target:{" "}
          <code className="rounded bg-muted px-1 py-0.5 font-mono">
            {plan.cnameTarget}
          </code>
        </p>
      </header>

      <ol className="space-y-3">
        {plan.steps.map((step, index) => (
          <CoexistenceStepCard key={step.kind} step={step} index={index} />
        ))}
      </ol>

      <footer className="rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
        After saving the records above, return to{" "}
        <a href="/settings/domains" className="underline">
          custom domains
        </a>{" "}
        and re-run the verification check. Coexistence mode does not modify
        your existing MX or SPF records — your current email provider keeps
        working.
      </footer>
    </section>
  );
}

function CoexistenceStepCard({
  step,
  index,
}: {
  step: CoexistenceInstructionStepView;
  index: number;
}) {
  return (
    <li
      data-testid={`coexistence-step-${step.kind}`}
      className="rounded-md border border-border p-3"
    >
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold uppercase text-muted-foreground">
          Step {index + 1}
        </span>
        <span
          className={`rounded px-1.5 py-0.5 text-xs font-mono ${RECORD_TYPE_BADGE[step.recordType]}`}
        >
          {step.recordType}
        </span>
      </div>
      <p className="mt-1 text-sm font-medium text-foreground">{step.heading}</p>
      <p className="mt-1 text-xs text-muted-foreground">{step.body}</p>
      <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
        <div>
          <dt className="font-medium text-muted-foreground">Record name</dt>
          <dd className="font-mono break-all text-foreground">
            {step.recordName}
          </dd>
        </div>
        <div>
          <dt className="font-medium text-muted-foreground">Record value</dt>
          <dd className="font-mono break-all text-foreground">
            {step.recordValue}
          </dd>
        </div>
      </dl>
      <p className="mt-3 text-xs text-muted-foreground">
        {step.navigationHint}
      </p>
    </li>
  );
}
